// Single source of truth for the logged-in user's access: the level (role)
// and the grants. Both come off the login response and are persisted to
// sessionStorage (alongside the bearer token) so gating survives a page
// refresh. Read here, never from props — see can() below.

const GRANTS_KEY = "grants";

// The level: "ADMIN", "USER" or "READONLY".
export function getRole() {
  return sessionStorage.getItem("role") || null;
}

// Admin-feature gate: Admin module, Agent management, etc. The level, not a
// grant — an admin gets everything, so can() is true for them too.
export function isAdmin() {
  return getRole() === "ADMIN";
}

// The account's grants as the backend names them: FINANCE, TRANSPORT,
// SUPERVISOR, AGENT. Empty for most accounts.
export function getGrants() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(GRANTS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// The one permission check: can("finance"), can("transport"), ... Case
// doesn't matter. ADMIN passes every grant; nobody else passes one they
// don't hold. Whether a grant hides a screen (finance) or only a control
// (transport) is the caller's business.
export function can(grant) {
  if (!grant) return true;
  if (isAdmin()) return true;
  return getGrants().includes(String(grant).toUpperCase());
}

// Persist what the login response told us. `showFinancials` is the older
// single flag; the backend still sets it for the pre-grant email allowlist,
// so until that list is deleted (roles work, step 7) it is folded into the
// grants as FINANCE and everything downstream reads one thing.
export function rememberLogin({ email, role, grants, showFinancials }) {
  const set = new Set((grants || []).map((g) => String(g).toUpperCase()));
  if (showFinancials) set.add("FINANCE");
  sessionStorage.setItem("userEmail", email || "");
  sessionStorage.setItem("role", role || "");
  sessionStorage.setItem(GRANTS_KEY, JSON.stringify([...set].sort()));
}

export function forgetLogin() {
  // The token goes too: a signed-out tab holding a credential is untidy at
  // best, and after someone else has signed in on this device it is the last
  // thing that should linger.
  sessionStorage.removeItem("bearer");
  sessionStorage.removeItem("isAuthenticated");
  sessionStorage.removeItem("role");
  sessionStorage.removeItem(GRANTS_KEY);
  sessionStorage.removeItem("showFinancials");
}
